// HASLO: back-in-stock sign-up (snippets/haslo-back-in-stock.liquid).
// Posts to Klaviyo's client back-in-stock endpoint with the store's public key. 202 means subscribed.
// Klaviyo only emails people if its back-in-stock flow is set up in the Klaviyo account.
// Strings come from data- attributes rendered from locales.

// Klaviyo API revision the request body is written against (not customer-facing, so not a setting).
const HASLO_KLAVIYO_REVISION = '2026-10-15';

class HasloBackInStock extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="email"]');
    this.button = this.querySelector('button[type="submit"]');
    this.status = this.querySelector('[data-haslo-status]');
    this.form.addEventListener('submit', (event) => this.submit(event));
    this.button.disabled = false;

    // Product page: follow Focal's variant picker (it fires variant:changed on the product form)
    if (this.dataset.formId) {
      this.onVariantChanged = (event) => this.followVariant(event);
      document.addEventListener('variant:changed', this.onVariantChanged);
    }
  }

  disconnectedCallback() {
    if (this.onVariantChanged) document.removeEventListener('variant:changed', this.onVariantChanged);
  }

  followVariant(event) {
    if (event.target?.getAttribute?.('id') !== this.dataset.formId) return;

    const variant = event.detail?.variant;
    const soldOut = Boolean(variant && variant.id && !variant.available);
    this.hidden = !soldOut;
    if (soldOut) {
      this.setVariant(variant.id, {
        productId: this.dataset.productId,
        productTitle: this.dataset.productTitle,
        productUrl: this.dataset.productUrl,
        variantTitle: variant.title,
      });
    }
  }

  // Lets one form be reused for different variants (e.g. a sold-out size in quick add)
  setVariant(variantId, details = {}) {
    this.dataset.variantId = variantId;
    for (const key of ['productId', 'productTitle', 'productUrl', 'variantTitle']) {
      this.dataset[key] = details[key] || '';
    }
    this.removeAttribute('subscribed');
    this.show('', '');
  }

  async submit(event) {
    event.preventDefault();

    const email = this.input.value.trim();
    if (!email || !this.input.checkValidity()) {
      this.show(this.dataset.invalid, 'error');
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
      return;
    }
    this.input.removeAttribute('aria-invalid');

    const companyId = this.dataset.companyId;
    const variantId = this.dataset.variantId;
    if (!companyId || !variantId) {
      console.warn('[haslo-back-in-stock] Missing Klaviyo public key or variant id.');
      this.show(this.dataset.error, 'error');
      return;
    }

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    this.show('', '');

    try {
      const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
          accept: 'application/vnd.api+json',
          revision: HASLO_KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
            },
          },
        }),
      });

      if (response.status !== 202) throw new Error(`Klaviyo responded ${response.status}`);

      this.setAttribute('subscribed', '');
      this.track(companyId, email, variantId);
      this.input.value = '';
      this.input.dispatchEvent(new Event('input', { bubbles: true })); // lets Focal un-float the label
      this.show(this.dataset.success, 'success');
    } catch (error) {
      console.warn('[haslo-back-in-stock]', error);
      this.show(this.dataset.error, 'error');
    } finally {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
    }
  }

  // Records where the sign-up happened as a Klaviyo event, so it can be segmented on.
  // Fire and forget: the subscription already succeeded, so a failure here never reaches the customer.
  track(companyId, email, variantId) {
    const name = this.dataset.eventName;
    if (!name) return;

    fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'content-type': 'application/vnd.api+json',
        accept: 'application/vnd.api+json',
        revision: HASLO_KLAVIYO_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name } } },
            profile: { data: { type: 'profile', attributes: { email } } },
            properties: {
              source: this.dataset.source || '',
              product_id: this.dataset.productId || '',
              product_name: this.dataset.productTitle || '',
              variant_id: variantId,
              variant_name: this.dataset.variantTitle || '',
              product_url: this.dataset.productUrl || '',
              page_url: window.location.origin + window.location.pathname, // no query string: never send what's in the URL
            },
          },
        },
      }),
    })
      .then((response) => {
        if (response.status !== 202) console.warn('[haslo-back-in-stock] Sign-up event not recorded:', response.status);
      })
      .catch((error) => console.warn('[haslo-back-in-stock] Sign-up event not recorded:', error));
  }

  show(message, state) {
    this.status.textContent = message || '';
    this.status.dataset.state = state || '';
  }
}

if (!customElements.get('haslo-back-in-stock')) {
  customElements.define('haslo-back-in-stock', HasloBackInStock);
}

// Quick add: a sold-out size ([data-haslo-notify]) opens the shared dialog for that variant.
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-haslo-notify]');
  const dialog = document.getElementById('haslo-bis-dialog');
  if (!trigger || !dialog) return;

  event.preventDefault();
  dialog.querySelector('[data-haslo-bis-product]').textContent = trigger.dataset.notifyLabel || '';
  dialog.querySelector('haslo-back-in-stock')?.setVariant(trigger.dataset.variantId, trigger.dataset);
  dialog.showModal();
  dialog.querySelector('input[type="email"]')?.focus();
});

// Close on the close button or a click on the backdrop
document.addEventListener('click', (event) => {
  const dialog = document.getElementById('haslo-bis-dialog');
  if (!dialog?.open) return;
  if (event.target === dialog || event.target.closest('[data-haslo-bis-close]')) dialog.close();
});
